import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { strings } from '../../resources/strings';
import { ContactType } from '../../enums/ContactType';
import { ResultType } from '../../enums/ResultType';
import { InternalCommunicationType } from '../../enums/InternalCommunicationType';
import { StateOrderIdentification } from '../../enums/StateOrderIdentification';
import { CollectTrackingResultType } from '../../enums/CollectTrackingResultType';
import { StateLog } from '../../models/StateLog';
import { ShippingLine } from '../../models/ShippingLine';
import { Carrier } from '../../models/Carrier';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { GetErpOrderNFeInput } from '../../services/Orders/GetErpOrderNFe/GetErpOrderNFeInput';
import { AddInternalCommunicationInput } from '../../services/InternalCommunications/AddInternalCommunication/AddInternalCommunicationInput';
import { EnvironmentHelper } from '../../helpers/EnvironmentHelper';
import { Fulfillment } from '../../models/Fulfillment';
import { Button } from '../../common-components/Button/Button';
import { TabMenu } from '../../common-components/TabMenu/TabMenu';
import { Field, FieldType } from '../../common-components/Field/Field';
import { WaitingIcon } from '../../common-components/WaitingIcon/WaitingIcon';
import { TabStatusCarrier } from './TabStatusCarrier/TabStatusCarrier';
import { TabInternalCommunication } from './TabInternalCommunication/TabInternalCommunication';
import { FormShippingLine, FormShippingLineValues } from './FormShippingLine/FormShippingLine';
import { FormInternalCommunication, FormInternalCommunicationValues } from './FormInternalCommunication/FormInternalCommunication';
import { FooterContent, WaitingContainer } from './styles';
import { SessionHelper } from '../../helpers/SessionHelper'; 

interface IModalManageCarrierProps {
  visible: boolean;
  close: () => void;
  title: string;
  orderId?: number;
  orderNumber?: string;
}

const TabManagerCarrierOptions = {
  Tracking: 'tab-tracking',
  Communications: 'tab-communications'
}

const ModalManageCarrier: React.FC<IModalManageCarrierProps> = ({
  visible,
  close,
  title,
  orderId,
  orderNumber
}) => {

  const {session} = useAuth();
  const [activeTab, setActiveTab] = React.useState<string>(TabManagerCarrierOptions.Tracking);
  const [formInternalCommunicationVisible, setFormInternalCommunicationVisible] = React.useState<boolean>(false);
  const [formInternalCommunicationValues, setFormInternalCommunicationValues] = React.useState<FormInternalCommunicationValues | undefined>(undefined);
  const [update, setUpdate] = React.useState<boolean>(false);
  const [formManuallyCollectVisible, setFormManuallyCollectVisible] = React.useState<boolean>(false);
  const [formShippingLineValues, setFormShippingLineValues] = React.useState<FormShippingLineValues | undefined>(undefined);
  const [formShippingLineVisible, setFormShippingLineVisible] = React.useState<boolean>(false);
  const [numberNF, setNumberNF] = React.useState<string>('');
  const [shippingLineList, setShippingLineList] = React.useState<ShippingLine[]>([]);
  const [loadingShippingLineList, setLoadingShippingLineList] = React.useState<boolean>(true);
  const [lastStateLog, setLastStateLog] = React.useState<StateLog|undefined|null>(null);
  const [loadingLastStateLog, setLoadingLastStateLog] = React.useState<boolean>(true);
  const [carrierList, setCarrierList] = React.useState<Carrier[]>([]);
  const [fulfillments, setFulfillments] = React.useState<Fulfillment[]>([]);
  const [loadingFulfillments, setLoadingFulfillments] = React.useState<boolean>(true);

  const {
    toastService,
    alertService,
    addInternalCommunicationService,
    getErpNfService,
    getErpOrderNFeService,
    consultTrackingOrderService,
    getShippingLinesByOrderService,
    getCarrierListService,
    setShippingLineOrderService,
    recollectOrderService,
    getStateLogByOrderService,
    statesService,
    changeTrackingCodeService,
    getFulfillmentsByOrderService
  } = React.useContext(ServicesContext);

  const userCanManageTransport = SessionHelper.canManageTransport(session);
  
  React.useEffect(() => {
    getCarrierList();
  }, []);

  React.useEffect(() => {
    getShippingLinesWithCollectTrackings();
    getLastStateLog();
    getFulfillmentsByOrder();
  }, [orderId]);

  const memoBody = React.useMemo(() => {
    if (!visible) return null;

    if (update || loadingShippingLineList || loadingLastStateLog || loadingFulfillments) {
      return (
        <WaitingContainer>
          <WaitingIcon />
        </WaitingContainer>
      )
    }

    if (formShippingLineVisible) {
      return (
        <FormShippingLine
          carriers={carrierList}
          values={formShippingLineValues}
          change={(values) => changeFormShippingLineValues(values)}
        />
      )
    }

    if (formManuallyCollectVisible) {
      return (
        <Field
          type={FieldType.Text}
          label={'Informe o número da nota fiscal conforme está no Omie'}
          value={numberNF}
          change={(value) => setNumberNF(value)}
        />
      )
    }

    if (formInternalCommunicationVisible) {
      return (
        <FormInternalCommunication
          values={formInternalCommunicationValues}
          change={(values) => changeFormInternalCommunicationValues(values)}
        />
      );
    }

    const tab = activeTab === TabManagerCarrierOptions.Communications
      ? <TabInternalCommunication orderId={orderId} />
      : <TabStatusCarrier
        orderId={orderId}
        manuallyCollect={collectHandle}
        orderNumber={orderNumber}
        shippingLineList={shippingLineList}
        changeCarrier={(shippingLineId) => changeShippingLineOrder(shippingLineId)}
        changeTrackingCode={changeTrackingCode}
        fulfillments={fulfillments}
      />;

    const tabs = [
      {
        key: TabManagerCarrierOptions.Tracking,
        text: strings.StatusTransport
      }
    ];

    if (statesService.stateIdIsDeliverable(lastStateLog?.currentStateId)) {
      tabs.push({
        key: TabManagerCarrierOptions.Communications,
        text: strings.InternalCommunication
      });
    }

    return (
      <>
        <TabMenu
          tabs={tabs}
          activeKey={activeTab}
          change={(key) => setActiveTab(key)}
        />
        {tab}
      </>
    );
  }, [
    visible, activeTab, formInternalCommunicationVisible,
    update, formInternalCommunicationValues, formManuallyCollectVisible, numberNF,
    loadingShippingLineList, shippingLineList, formShippingLineVisible, formShippingLineValues,
    lastStateLog, loadingLastStateLog, carrierList, fulfillments, loadingFulfillments
  ]);

  const memoFooter = React.useMemo(() => {
    if (!visible || update || loadingShippingLineList || loadingLastStateLog || loadingFulfillments) return null;

    if (!userCanManageTransport) return null;

    let buttons: any = null;

    if (formShippingLineVisible) {
      buttons = (
        <>
          <Button text={strings.Cancel} marginRight='20px' click={() => cancelShippingLine()} />
          <Button text={strings.ConfirmShippingLine} click={() => saveShippingLine()} />
        </>
      );
    }

    if (formManuallyCollectVisible) {
      buttons = (
        <>
          <Button text={'Cancelar'} marginRight='20px' click={() => cancelManuallyCollect()} />
          <Button text={'Obter Nota Fiscal e Realizar Coleta'} click={() => getNFeAndCollectOrder()} />
        </>
      );
    }

    if (formInternalCommunicationVisible && !buttons) {
      buttons = (
        <>
          <Button text={strings.Cancel} width='190px' marginRight='20px' click={() => cancelFormInternalCommunication()} />
          <Button text={strings.ConfirmCommunication} width='190px' click={() => saveNewInternalCommunication()} />
        </>
      );
    }

    if (activeTab === TabManagerCarrierOptions.Communications && !buttons) {
      buttons = (
        <>
          <Button text={strings.NewCommunication} width='190px' marginRight='20px' click={() => insertInternalCommunication()} />
          <Button text={strings.Update} width='190px' click={() => updateModal()} />
        </>
      );
    }

    if (activeTab === TabManagerCarrierOptions.Tracking && !buttons) {
      if (shippingLineList.length === 0) {
        buttons = (
          <>
            <Button text={strings.SetShippingLine} click={() => setShippingLineOrder()} />
          </>
        );
      } else {
        const showPerformVirtualCollectButton = lastStateLog?.currentStateId === StateOrderIdentification.Enviado && 
          shippingLineList.every(sl => !sl.collectTrackingList || sl.collectTrackingList.length === 0);
        buttons = (
          <>
            <Button text={strings.Update} click={() => updateTracking()} /> 
            {showPerformVirtualCollectButton && <Button text={strings.PerformVirtualCollect} marginLeft='20px' click={() => recollect()} />}
          </>
        );
      }
    }

    if (buttons) {
      return (
        <Modal.Footer>
          <FooterContent>
            {buttons}
          </FooterContent>
        </Modal.Footer>
      );
    }

    return null;
  }, [
    visible, activeTab, formInternalCommunicationVisible,
    update, formInternalCommunicationValues, formManuallyCollectVisible, numberNF,
    loadingShippingLineList, shippingLineList, formShippingLineVisible, , formShippingLineValues,
    lastStateLog, loadingLastStateLog, carrierList, fulfillments, loadingFulfillments
  ]);

  if (!visible) return null;

  return (
    <Modal
      show={visible}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id='modalManageCarrierBody' style={{ maxHeight: '78vh', overflowY: 'auto' }}>
        {memoBody}
      </Modal.Body>

      {memoFooter}
    </Modal>
  );

  function getCarrierList() {
    getCarrierListService.handle()
      .then(result => {
        setCarrierList(result?.value?.carriers || []);
      });
  }

  function getShippingLinesWithCollectTrackings() {
    if (!orderId || !visible) return;

    setLoadingShippingLineList(true);

    getShippingLinesByOrderService.handle({ orderId })
      .then(result => {
        setShippingLineList(result?.value?.shippingLines || []);
      })
      .finally(() => {
        setLoadingShippingLineList(false);
      });
  }

  function getLastStateLog() {
    if (!orderId || !visible) return;

    setLoadingLastStateLog(true);

    getStateLogByOrderService.handle({orderId})
      .then(result => {
        const stateLogs = result?.value?.stateLogs || [];
        if (stateLogs.length > 0)
          setLastStateLog(stateLogs[stateLogs.length-1]);
      })
      .finally(() => {
        setLoadingLastStateLog(false);
      });
  }

  function getFulfillmentsByOrder() {
    if (!orderId || !visible) return;

    getFulfillmentsByOrderService.handle({ orderId, updateTrackingNumber: true })
      .then(result => {
        setFulfillments(result?.value?.fulfillments || []);
      })
      .finally(() => {
        setLoadingFulfillments(false);
      });
  }

  function clickCloseModal() {
    setFormInternalCommunicationVisible(false);
    setActiveTab(TabManagerCarrierOptions.Tracking);
    close();
  }

  function insertInternalCommunication() {
    setFormInternalCommunicationValues(undefined);
    setFormInternalCommunicationVisible(true);
  }

  function cancelFormInternalCommunication() {
    setFormInternalCommunicationVisible(false);
    setFormInternalCommunicationValues(undefined);
  }

  function saveNewInternalCommunication() {
    if (!formInternalCommunicationValues?.message) {
      toastService.warning(strings.EnterMessage);
      return;
    }

    const input = new AddInternalCommunicationInput();
    input.internalCommunicationLog.relatedOrderId = orderId;
    input.internalCommunicationLog.relatedOrderNumber = orderNumber;
    input.internalCommunicationLog.contactType = ContactType.Slack;
    input.internalCommunicationLog.internalCommunicationType = InternalCommunicationType.CarrierStatusAlert;
    input.internalCommunicationLog.message = formInternalCommunicationValues.message;
    input.internalCommunicationLog.resultType = ResultType.Pending;
    input.internalCommunicationLog.contact = EnvironmentHelper.isProduction()
      ? 'comunicação-interna-sislog'
      : 'comunicação-interna-sislog-dev';
    addInternalCommunicationService.handle(input)
      .then(result => {
        cancelFormInternalCommunication();
        updateModal();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
  }

  function updateTracking() {
    if (!orderId) return;

    const orderHaveAnyShippingLineWithCollectTracking = shippingLineList.some(sll => 
      sll.orderId === orderId && 
      sll.collectTrackingList && 
      sll.collectTrackingList.length > 0 && 
      sll.collectTrackingList.some(ctl => ctl.collectTrackingResultType === CollectTrackingResultType.Success));

    if (!orderHaveAnyShippingLineWithCollectTracking) {
      getShippingLinesWithCollectTrackings();
      return;
    }

    consultTrackingOrderService.handle({ orderId: orderId })
      .then(result => {
        getShippingLinesWithCollectTrackings();
      });
  }

  function changeShippingLineOrder(shippingLineId: number) {
    const shippingLine = shippingLineList.find(sll => sll.id === shippingLineId);
    if (!shippingLine) return;
    setFormShippingLineVisible(true);
    setFormShippingLineValues({ shippingLineId });
  }

  function setShippingLineOrder() {
    if (!orderId) return;

    setFormShippingLineVisible(true);
    setFormShippingLineValues(undefined);
  }

  function updateModal() {
    setUpdate(true);
    setTimeout(() => setUpdate(false), 500);
  }

  function changeFormInternalCommunicationValues(values: FormInternalCommunicationValues) {
    setFormInternalCommunicationValues(values);
  }

  function changeFormShippingLineValues(values: FormShippingLineValues) {
    setFormShippingLineValues(values);
  }

  function collectHandle(getNfe: boolean) {
    if (getNfe) {
      startManuallyCollect();
      return;
    }

    recollect();
  }

  function recollect() {
    if (!orderId) return;

    setUpdate(true);

    recollectOrderService.handle({orderId})
      .then(result => {
        if (result?.value?.result) {
          toastService.info('Pronto! Agora é só aguardar a coleta virtual automática.');
          return;
        }
        alertService.warning(
          'Pode ter acontecido algum problema. '+
          'Se a coleta virtual não for realizada tente novamente e '+
          'se o problema persistir avise o time Tech.');
      })
      .catch(error => {
        alertService.warning(
          'Pode ter acontecido algum problema. '+
          'Se a coleta virtual não for realizada tente novamente e '+
          'se o problema persistir avise o time Tech.');
      })
      .finally(() => {
        setUpdate(false);
        getShippingLinesWithCollectTrackings();
      });
  }

  function startManuallyCollect() {
    setNumberNF('');
    setFormManuallyCollectVisible(true);
  }

  function cancelManuallyCollect() {
    setNumberNF('');
    setFormManuallyCollectVisible(false);
  }

  async function getNFeAndCollectOrder() {
    if (!orderId) return;

    if (!numberNF) {
      toastService.warning('Informe o número da nota fiscal');
      return;
    }

    setUpdate(true);

    const getNfResult = await getErpNfService.handle({ number: numberNF });

    setUpdate(false);

    const erpOrderId = getNfResult?.value?.nf?.complement?.orderId;
    const erpNfeKey = getNfResult?.value?.nf?.complement?.nfeKey;
    const erpNfValue = getNfResult?.value?.nf?.total?.icmsTotal?.nfValue;

    if (!erpOrderId || !erpNfeKey || erpNfValue === undefined || erpNfValue === null) {
      toastService.warning('Nota fiscal não encontrada.');
      return;
    }

    const message = `Nota fiscal encontrada: <br>Chave: ${erpNfeKey} <br>Valor: ${erpNfValue.toFixed(2)} <br>Confirma a nota fiscal?`;
    const confirmResult = await alertService.confirmHtml(message);

    if (!confirmResult.isConfirmed) {
      cancelManuallyCollect();
      return;
    }

    setUpdate(true);

    const input = new GetErpOrderNFeInput();
    input.orderId = orderId;
    input.erpOrderCode = erpOrderId;
    input.collectOrder = true;
    const getErpOrderResult = await getErpOrderNFeService.handle(input);

    setUpdate(false);

    if (!getErpOrderResult?.value?.result) {
      toastService.warning('Não foi possível salvar a nota fiscal e realizar a coleta. Avise o time Tech.');
      return;
    }

    toastService.info('Nota fiscal salva com sucesso. Agora aguarde a coleta virtual automática.');

    cancelManuallyCollect();
    getShippingLinesWithCollectTrackings();
  }

  function cancelShippingLine() {
    setFormShippingLineVisible(false);
    setFormShippingLineValues(undefined);
  }

  function saveShippingLine() {
    if (!orderId) return;

    if (!formShippingLineValues?.code) {
      toastService.info('Informe a transportadora.');
      return;
    }

    if (formShippingLineValues && formShippingLineValues.shippingLineId) {
      const shippingLine = shippingLineList.find(sll => sll.id === formShippingLineValues.shippingLineId);
      if (shippingLine?.code === formShippingLineValues?.code) {
        toastService.info('Informe uma transportadora diferente da atual.');
        return;
      }
    }

    setShippingLineOrderService.handle({
      orderId,
      code: formShippingLineValues.code,
      shippingLineId: formShippingLineValues?.shippingLineId,
      title: carrierList.find(c => c.code === formShippingLineValues.code)?.name || formShippingLineValues.code,
      userId: session?.user?.id
    }).then(result => {
      setFormShippingLineVisible(false);
      setFormShippingLineValues(undefined);

      if (!result?.value?.result) {
      }

      getShippingLinesWithCollectTrackings();
    }).catch(() => {
      toastService.warning(strings.OpsSomethingWrongSendTheData);
    });
  }

  function changeTrackingCode(collectTrackingItemId: number, trackingCode: string) {
    changeTrackingCodeService.handle({ 
      collectTrackingItemId,
      trackingCode,
      userId: session?.user?.id
    }).then(result => {
      getShippingLinesWithCollectTrackings();
      getLastStateLog();
    }).catch(() => {
      toastService.warning(strings.OpsSomethingWrongSendTheData);
    });
  }
}

export { ModalManageCarrier };